import axiosClient from '../../api/axiosClient.js';
import { fetchPageData } from '../../api/utils.js';
import AuthService from '../../auth/AuthService';

async function getGeneralInfo() {
  const user = AuthService.getUser();
  const url = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const generalInfoQuery = () => {
  return {
    queryKey: ['generalInfo'],
    queryFn: async () => getGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

// Trimmed general-info payload for the summary pages. Backend early-
// exits when any of the `?principles / ?classification / ?normal /
// ?balancedequation` params is present, returning only `games`
// (id / game_type / name / scenario) and `sessions` (id / user / game
// / completed / attempt) filtered to the current user + selected game
// type(s). Distinct queryKey so it doesn't clash with the full
// generalInfoQuery cache used by game-play pages.
async function getSummaryGeneralInfo() {
  const user = AuthService.getUser();
  const base = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(`${base}?normal`);
  return data;
}

export const generalInfoSummaryQuery = () => {
  return {
    queryKey: ['generalInfo', 'summary', 'normal'],
    queryFn: async () => getSummaryGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

async function getConsolidatedScores() {
  const { data } = await axiosClient.get(`/api/scores/`);
  return data;
}

export const consolidatedScoresQuery = () => {
  return {
    queryKey: ['consolidatedScores'],
    queryFn: async () => getConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

// Trimmed consolidated-scores payload for the summary pages. Always
// scopes to the matching game type (`?normal` here) so the response
// only carries the bucket the page reads. When the caller is an
// instructor, appends `&user=<their_id>` so the endpoint returns just
// the instructor's own scores instead of all students' scores across
// the course. Distinct queryKey (includes the instructor id when
// applicable) so this cache stays separate from consolidatedScoresQuery
// and doesn't cross-pollinate if the same user later switches accounts.
async function getSummaryConsolidatedScores() {
  const user = AuthService.getUser();
  const params = ['normal'];
  if (user?.is_instructor && user?.id != null) {
    params.push(`user=${user.id}`);
  }
  const { data } = await axiosClient.get(`/api/scores/?${params.join('&')}`);
  return data;
}

export const consolidatedScoresSummaryQuery = () => {
  const user = AuthService.getUser();
  const scopedUserId = user?.is_instructor ? (user?.id ?? null) : null;
  return {
    queryKey: ['consolidatedScores', 'summary', 'normal', scopedUserId],
    queryFn: async () => getSummaryConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

async function getConsolidatedInsights({userId} = {}) {
  const url = userId ? `/api/insights/?user=${userId}` : `/api/insights/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const consolidatedInsightsQuery = ({userId} = {}) => {
  return {
    queryKey: ['consolidatedInsights', userId ?? null],
    queryFn: async () => getConsolidatedInsights({userId}),
    refetchOnWindowFocus: false
  };
};

async function fetchGamesPage(url, allGames = []) {
  const response = await axiosClient.get(url);
  const games = [...allGames, ...response.data.results];
  
  if (response.data.next) {
    return fetchGamesPage(response.data.next, games);
  } else {
    return games;
  }
}

async function getGames() {
  return fetchGamesPage(`/api/games/games/`);
}

export const gamesQuery = () => {
  return {
    queryKey: ['games'],
    queryFn: async () => getGames(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function fetchScenarioLevelsPage(url, allScenarioLevels = []) {
  const response = await axiosClient.get(url);
  const scenarioLevels = [...allScenarioLevels, ...response.data.results];
  
  if (response.data.next) {
    return fetchScenarioLevelsPage(response.data.next, scenarioLevels);
  } else {
    return scenarioLevels;
  }
}

async function getScenarioLevels(scenarioId) {  
  return scenarioId ? fetchScenarioLevelsPage(`/api/games/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: ['scenarioLevels', scenarioId],
    queryFn: async () => getScenarioLevels(scenarioId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!scenarioId
  };
};

async function getScenarioLevel({scenarioId, level}) {
  const response = await axiosClient.get(`/api/games/scenario-levels/?scenarios=${scenarioId}&level=${level}`);
  return response.data.results[0];
}

export const scenarioLevelQuery = ({scenarioId, level}) => {
  return {
    queryKey: ['scenarioLevel', scenarioId],
    queryFn: async () => getScenarioLevel({scenarioId, level}),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: !!scenarioId
  };
};

async function fetchItemOptionsPage(url, allItemOptions = []) {
  const response = await axiosClient.get(url);
  const itemOptions = [...allItemOptions, ...response.data.results];
  
  if (response.data.next) {
    return fetchItemOptionsPage(response.data.next, itemOptions);
  } else {
    return itemOptions;
  }
}

async function getItemOptions() {  
  return fetchItemOptionsPage(`/api/normal/options/`);
}

export const itemOptionsQuery = () => {
  return {
    queryKey: ['itemOptions'],
    queryFn: async () => getItemOptions(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function fetchItemQuestionsPage(url, allItemQuestions = []) {
  const response = await axiosClient.get(url);
  const itemQuestions = [...allItemQuestions, ...response.data.results];
  
  if (response.data.next) {
    return fetchItemQuestionsPage(response.data.next, itemQuestions);
  } else {
    return itemQuestions;
  }
}

async function getItemQuestions(scenarioLevelId) {  
  return scenarioLevelId ? fetchItemQuestionsPage(`/api/normal/questions/?scenario_level=${scenarioLevelId}`) : [];
}

export const itemQuestionsQuery = ({scenarioLevelId}) => {
  return {
    queryKey: ['itemQuestions', scenarioLevelId],
    queryFn: async () => getItemQuestions(scenarioLevelId),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    enabled: !!scenarioLevelId
  };
};

async function fetchItemsPage(url, allItems = []) {
  const response = await axiosClient.get(url);
  const items = [...allItems, ...response.data.results];
  
  if (response.data.next) {
    return fetchItemsPage(response.data.next, items);
  } else {
    return items;
  }
}

async function getItems() {
  return fetchItemsPage(`/api/games/items/`);
}

export const itemsQuery = () => {
  return {
    queryKey: ['items'],
    queryFn: async () => getItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function makeDecision({user, game, question, selected_option, time_elapsed, timestamp, gameSessionId}) {
  const { data } = await axiosClient.post(`api/normal/decisions/`, {
    user,
    game,
    question,
    selected_option,
    time_elapsed: time_elapsed || 1,
    timestamp,
    game_session: gameSessionId
  });

  return data;
}

async function fetchDecisionsPage(url, allDecisions = []) {
  const response = await axiosClient.get(url);
  const decisions = [...allDecisions, ...response.data.results];
  
  if (response.data.next) {
    return fetchDecisionsPage(response.data.next, decisions);
  } else {
    return decisions;
  }
}

export async function getDecisions({levelNumber, userId, gameId, gameSessionId}) {
  return fetchDecisionsPage(`/api/normal/decisions/?level=${levelNumber}&user=${userId}&game=${gameId}&game_session=${gameSessionId}`);
}

async function fetchGamesSessions(url, allSessions = []) {
  const response = await axiosClient.get(url);
  const sessions = [...allSessions, ...response.data.results];
  
  if (response.data.next) {
    return fetchGamesSessions(response.data.next, sessions);
  } else {
    return sessions;
  }
}

export async function getGamesSessions() {
  return fetchGamesSessions(`/api/games/sessions/`);
}

export async function getScoresStats({gameId, attempt}) {
  const response = await axiosClient.get(`/api/normal/scores/stats/${gameId}/?attempt=${attempt}`);
  return response.data;
}

export const scoresStatsQuery = ({gameId, attempt}) => {
  return {
    queryKey: ['scoresStats'],
    queryFn: async () => getScoresStats({gameId, attempt}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!attempt
  };
};

async function fetchScoresByQuestion(url, allScores = []) {
  const response = await axiosClient.get(url);
  const scores = [...allScores, ...response.data.results];
  
  if (response.data.next) {
    return fetchScoresByQuestion(response.data.next, scores);
  } else {
    return scores;
  }
}

export async function getScoresByQuestion({gameId, gameSessionId}) {
  return fetchScoresByQuestion(`/api/normal/scores/by-question/?game=${gameId}&game_session=${gameSessionId}`);
}

export const scoresByQuestionQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: ['scoresByQuestion'],
    queryFn: async () => getScoresByQuestion({gameId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!gameSessionId
  };
};

export async function updateGameSession({id, levelNumber, status, isSessionCompleted}) {
  const levelKey = `level${levelNumber}`;
  const requestBody = {
    [levelKey]: status,
  };

  if (isSessionCompleted) {
    requestBody.completed = true;
  }

  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, requestBody);

  return data;
}

export async function getScores({gameId, userId, gameSessionId}) {
  return fetchPageData(`/api/normal/scores/?game=${gameId}&user=${userId}&game_session=${gameSessionId}`);
}

export const scoresQuery = ({gameId, userId, gameSessionId}) => {
  return {
    queryKey: [`sessions_${gameSessionId}`],
    queryFn: async () => getScores({gameId, userId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!userId && !!gameSessionId
  };
};

export async function getScoresInsights({gameId, gameSessionId}) {
  return fetchPageData(`/api/normal/scores/insights/?game=${gameId}&game_session=${gameSessionId}`);
}

export const scoresInsightsQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: [`scoresInsights`],
    queryFn: async () => getScoresInsights({gameId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!gameSessionId
  };
};

export async function getDecisionsInsights({gameId, gameSessionId}) {
  const response = await axiosClient.get(`/api/normal/decisions/insights/${gameId}/${gameSessionId}`);
  return response?.data;
}

export const decisionsInsigtsQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: [`normalDecisionsInsights`],
    queryFn: async () => getDecisionsInsights({gameId, gameSessionId}),
    enabled: !!gameId && !!gameSessionId,
    refetchOnWindowFocus: false
  }
}

export async function updateFeedback({id, feedback1, feedback2, feedback3}) {
  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, {
    feedback1,
    feedback2,
    feedback3
  });

  return data;
}

export async function getTournamentsRounds() {
  return fetchPageData(`/api/tournaments/rounds/`);
}

export const tournamentsRoundsQuery = () => {
  return {
    queryKey: ['tournamentsRoundsNormal'],
    queryFn: async () => getTournamentsRounds(),
    refetchOnWindowFocus: false
  };
};

async function getScenarioLevelsItemsView(scenarioId) {
  const response = await axiosClient.get(`/api/games/scenario-levels/items-view/?scenarios=${scenarioId}`);
  return response.data;
}

export const scenarioLevelsItemsViewQuery = (scenarioId) => {
  return {
    queryKey: ['scenarioLevelsItemsView', scenarioId],
    queryFn: async () => getScenarioLevelsItemsView(scenarioId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!scenarioId
  };
};

export async function postDecisionsBulk(decisions) {
  const { data } = await axiosClient.post(`api/normal/decisions/bulk/`, decisions);
  return data;
}